import * as THREE from 'three';

export default class FloatingCube {
    constructor(scene, physics, position = { x: 0, z: 0 }, audioManager = null) {
        this.scene = scene;
        this.physics = physics;
        this.position = position;
        this.audioManager = audioManager;
        
        // Cube properties
        this.size = 5 + Math.random() * 2; // Random size between 5 and 7
        this.baseHeight = 4; // Bus height
        this.maxHeight = 12; // Increased max height for more vertical challenge
        this.currentHeight = this.baseHeight + Math.random() * (this.maxHeight - this.baseHeight);
        
        // Floating animation properties
        this.floatAmplitude = 0.8 + Math.random() * 0.4; // More dramatic floating
        this.floatSpeed = 1.5 + Math.random() * 1; // Faster and more varied speed
        this.startTime = Date.now();
        this.waveOffset = Math.random() * Math.PI * 2; // For wave-like motion
        
        // Rotation properties
        this.baseRotationSpeed = 0.03;
        this.maxRotationSpeed = 0.15;
        this.rotationSpeedX = (Math.random() - 0.5) * this.baseRotationSpeed * 2;
        this.rotationSpeedY = (Math.random() - 0.5) * this.baseRotationSpeed * 2;
        this.rotationSpeedZ = (Math.random() - 0.5) * this.baseRotationSpeed * 2;
        this.proximityRadius = 30; // Increased radius for earlier reaction
        this.colorChangeRadius = 40; // Radius for color change effect
        
        // Create the cube
        this.create();
    }
    
    create() {
        // Create cube geometry
        const geometry = new THREE.BoxGeometry(this.size, this.size, this.size);
        
        // Create material with random color
        const hue = Math.random();
        const saturation = 0.7;
        const lightness = 0.5;
        const color = new THREE.Color().setHSL(hue, saturation, lightness);
        
        const material = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.4,
            metalness: 0.6,
            emissive: color.clone().multiplyScalar(0.2),
        });
        
        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.position.set(
            this.position.x,
            this.currentHeight,
            this.position.z
        );
        
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        
        // Add to scene
        this.scene.add(this.mesh);
        
        // Store reference to FloatingCube instance for collision detection
        this.mesh.userData.floatingCube = this;
        
        // Add physics collider
        this.createPhysicsCollider();
    }
    
    createPhysicsCollider() {
        if (!this.physics || !this.physics.isInitialized) return;
        
        const halfExtents = new THREE.Vector3(
            this.size / 2,
            this.size / 2,
            this.size / 2
        );
        
        // Create static box collider
        this.collider = this.physics.createStaticBox(
            new THREE.Vector3(this.position.x, this.currentHeight, this.position.z),
            halfExtents,
            0.3 // Lower friction for floating effect
        );
        
        // Store reference to mesh for collision visualization
        this.collider.visualMeshes = [this.mesh];
    }
    
    update(busPosition) {
        // Calculate floating motion with wave pattern
        const elapsedTime = (Date.now() - this.startTime) / 1000; // Convert to seconds
        const waveX = Math.sin(elapsedTime * this.floatSpeed + this.waveOffset) * (this.floatAmplitude * 0.5);
        const waveZ = Math.cos(elapsedTime * this.floatSpeed + this.waveOffset) * (this.floatAmplitude * 0.5);
        const floatOffset = Math.sin(elapsedTime * this.floatSpeed) * this.floatAmplitude;
        
        // Update position with wave-like motion
        this.mesh.position.y = this.currentHeight + floatOffset;
        this.mesh.position.x = this.position.x + waveX;
        this.mesh.position.z = this.position.z + waveZ;
        
        // Update collider position if it exists
        if (this.collider) {
            this.collider.position.copy(this.mesh.position);
        }
        
        // Calculate distance to bus if position is provided
        let rotationMultiplier = 1;
        let distance = Infinity;
        if (busPosition) {
            distance = Math.sqrt(
                Math.pow(this.mesh.position.x - busPosition.x, 2) +
                Math.pow(this.mesh.position.z - busPosition.z, 2)
            );
            
            // Increase rotation speed based on proximity
            if (distance < this.proximityRadius) {
                rotationMultiplier = 1 + ((this.proximityRadius - distance) / this.proximityRadius) * 6;
                
                // Add erratic movement when very close
                if (distance < this.proximityRadius * 0.5) {
                    this.rotationSpeedX += (Math.random() - 0.5) * 0.01;
                    this.rotationSpeedY += (Math.random() - 0.5) * 0.01;
                    this.rotationSpeedZ += (Math.random() - 0.5) * 0.01;
                }
            }
            
            // Change color based on proximity
            if (distance < this.colorChangeRadius) {
                const intensity = 1 - (distance / this.colorChangeRadius);
                const baseColor = this.mesh.material.color.clone();
                const warningColor = new THREE.Color(1, 0.3, 0.1); // Reddish warning color
                this.mesh.material.color.lerpColors(baseColor, warningColor, intensity);
                this.mesh.material.emissiveIntensity = 0.5 + intensity * 0.5;
            }
        }
        
        // Apply rotation with proximity-based speed
        this.mesh.rotation.x += this.rotationSpeedX * rotationMultiplier;
        this.mesh.rotation.y += this.rotationSpeedY * rotationMultiplier;
        this.mesh.rotation.z += this.rotationSpeedZ * rotationMultiplier;
    }

    hit() {
        // Play explosion sound if audio manager is available
        if (this.audioManager) {
            this.audioManager.play('cube_explode');
        }

        // Add 1% fuel when cube is hit
        if (window.game && window.game.bus) {
            const bus = window.game.bus;
            const fuelIncrease = bus.fuelCapacity * 0.01; // 1% of fuel capacity
            bus.fuel = Math.min(bus.fuel + fuelIncrease, bus.fuelCapacity);
            
            // Dispatch fuel update event
            const fuelEvent = new CustomEvent('fuel_updated', {
                detail: {
                    level: bus.fuel,
                    capacity: bus.fuelCapacity,
                    percentage: (bus.fuel / bus.fuelCapacity) * 100,
                    empty: bus.fuel <= 0
                }
            });
            window.dispatchEvent(fuelEvent);
        }

        // Create multiple cube fragments for explosion effect
        const fragmentCount = 8;
        const fragments = [];
        const fragmentSize = this.size / 2;
        
        // Create explosion flash
        const flashGeometry = new THREE.SphereGeometry(this.size * 0.8, 16, 16);
        const flashMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.8
        });
        const flash = new THREE.Mesh(flashGeometry, flashMaterial);
        flash.position.copy(this.mesh.position);
        this.scene.add(flash);
        
        // Create fragments
        for (let i = 0; i < fragmentCount; i++) {
            const geometry = new THREE.BoxGeometry(fragmentSize, fragmentSize, fragmentSize);
            const material = new THREE.MeshStandardMaterial({
                color: this.mesh.material.color,
                emissive: this.mesh.material.emissive,
                transparent: true,
                opacity: 1
            });
            
            const fragment = new THREE.Mesh(geometry, material);
            fragment.position.copy(this.mesh.position);
            
            // Add random rotation
            fragment.rotation.set(
                Math.random() * Math.PI * 2,
                Math.random() * Math.PI * 2,
                Math.random() * Math.PI * 2
            );
            
            // Random velocity with more upward bias
            fragment.velocity = new THREE.Vector3(
                (Math.random() - 0.5) * 8,
                Math.random() * 10 + 5, // More upward force
                (Math.random() - 0.5) * 8
            );
            
            // Random rotation speed
            fragment.rotationSpeed = new THREE.Vector3(
                (Math.random() - 0.5) * 0.2,
                (Math.random() - 0.5) * 0.2,
                (Math.random() - 0.5) * 0.2
            );
            
            this.scene.add(fragment);
            fragments.push(fragment);
        }
        
        // Add smaller particles for debris effect
        const particleCount = 15;
        const particles = [];
        
        for (let i = 0; i < particleCount; i++) {
            const geometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
            const material = new THREE.MeshStandardMaterial({
                color: this.mesh.material.color,
                emissive: this.mesh.material.emissive,
                transparent: true,
                opacity: 1
            });
            
            const particle = new THREE.Mesh(geometry, material);
            particle.position.copy(this.mesh.position);
            
            particle.velocity = new THREE.Vector3(
                (Math.random() - 0.5) * 12,
                Math.random() * 12,
                (Math.random() - 0.5) * 12
            );
            
            this.scene.add(particle);
            particles.push(particle);
        }
        
        // Animate explosion
        let elapsed = 0;
        const animate = () => {
            elapsed += 0.016; // Approximate for 60fps
            
            // Animate flash
            if (flash.material.opacity > 0) {
                flash.material.opacity -= 0.2;
                if (flash.material.opacity <= 0) {
                    this.scene.remove(flash);
                    flash.geometry.dispose();
                    flash.material.dispose();
                }
            }
            
            // Animate fragments
            fragments.forEach((fragment, index) => {
                // Apply gravity
                fragment.velocity.y -= 0.3;
                
                // Update position
                fragment.position.add(fragment.velocity.clone().multiplyScalar(0.016));
                
                // Update rotation
                fragment.rotation.x += fragment.rotationSpeed.x;
                fragment.rotation.y += fragment.rotationSpeed.y;
                fragment.rotation.z += fragment.rotationSpeed.z;
                
                // Fade out
                fragment.material.opacity -= 0.016;
                
                if (fragment.material.opacity <= 0) {
                    this.scene.remove(fragment);
                    fragment.geometry.dispose();
                    fragment.material.dispose();
                    fragments.splice(index, 1);
                }
            });
            
            // Animate particles
            particles.forEach((particle, index) => {
                particle.velocity.y -= 0.2; // Less gravity than fragments
                particle.position.add(particle.velocity.clone().multiplyScalar(0.016));
                particle.material.opacity -= 0.02;
                
                if (particle.material.opacity <= 0) {
                    this.scene.remove(particle);
                    particle.geometry.dispose();
                    particle.material.dispose();
                    particles.splice(index, 1);
                }
            });
            
            if (fragments.length > 0 || particles.length > 0) {
                requestAnimationFrame(animate);
            }
        };
        
        animate();
        this.remove();
    }
    
    remove() {
        // Remove from scene
        if (this.mesh) {
            this.scene.remove(this.mesh);
        }
        
        // Remove from physics world if collider exists
        if (this.physics && this.physics.bodies && this.collider) {
            const index = this.physics.bodies.indexOf(this.collider);
            if (index !== -1) {
                this.physics.bodies.splice(index, 1);
            }
            
            const colliderIndex = this.physics.colliders.indexOf(this.collider);
            if (colliderIndex !== -1) {
                this.physics.colliders.splice(colliderIndex, 1);
            }
        }
    }
}